/**
 * Expanded Player Component
 *
 * Full-screen player view with large artwork and controls.
 * Responsive for all screen sizes.
 */

import { usePlayerStore } from '@/stores/player';
import { useLikesStore } from '@/stores/likes';
import {
  PlayIcon,
  PauseIcon,
  SkipNextIcon,
  SkipPreviousIcon,
  ShuffleIcon,
  RepeatIcon,
  HeartIcon,
  HeartOutlineIcon,
  QueueMusicIcon,
  ChevronDownIcon,
} from '../icons';

export function ExpandedPlayer() {
  const {
    currentSong,
    isPlaying,
    progress,
    duration,
    shuffle,
    repeat,
    toggle,
    skipNext,
    skipPrevious,
    seek,
    toggleShuffle,
    toggleRepeat,
    setExpanded,
  } = usePlayerStore();

  const { isLiked, toggleLike } = useLikesStore();

  if (!currentSong) return null;

  const liked = isLiked(currentSong.id);

  // Get best thumbnail - support both array and direct string formats
  const thumbnails = currentSong.thumbnails || [];
  const thumbnail = currentSong.thumbnail || thumbnails[thumbnails.length - 1]?.url || '/pwa-512x512.webp';

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-player-expanded bg-black flex flex-col safe-area-inset">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 shrink-0">
        <button
          className="btn-icon"
          onClick={() => setExpanded(false)}
          aria-label="Minimize"
        >
          <ChevronDownIcon className="w-6 h-6" />
        </button>

        <div className="text-center">
          <p className="text-xs text-on-surface-variant uppercase tracking-wider">
            Now Playing
          </p>
        </div>

        <button className="btn-icon" aria-label="Queue">
          <QueueMusicIcon className="w-6 h-6" />
        </button>
      </header>

      {/* Main content - responsive layout */}
      <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 px-6 py-4 overflow-hidden">
        {/* Album art */}
        <div className="w-full max-w-xs sm:max-w-sm lg:max-w-md aspect-square shrink-0">
          <img
            src={thumbnail}
            alt={currentSong.title}
            className="w-full h-full object-cover rounded-2xl shadow-2xl"
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/pwa-512x512.webp';
            }}
          />
        </div>

        {/* Controls section */}
        <div className="w-full max-w-md lg:max-w-lg flex flex-col items-center gap-6">
          {/* Song info */}
          <div className="text-center w-full">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-on-surface line-clamp-2">
              {currentSong.title}
            </h1>
            <p className="text-base sm:text-lg text-on-surface-variant mt-2 line-clamp-1">
              {currentSong.artists?.map((a) => a.name).join(', ')}
            </p>
          </div>

          {/* Progress bar */}
          <div className="w-full">
            <input
              type="range"
              min={0}
              max={duration || 100}
              value={progress}
              onChange={(e) => seek(parseFloat(e.target.value))}
              className="w-full h-1 bg-surface-container-highest rounded-full appearance-none cursor-pointer
                         [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                         [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full
                         [&::-webkit-slider-thumb]:cursor-pointer"
            />
            <div className="flex justify-between mt-2 text-xs text-on-surface-variant">
              <span>{formatTime(progress)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Main controls */}
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            <button
              className={`btn-icon ${shuffle ? 'text-primary' : ''}`}
              onClick={toggleShuffle}
              aria-label="Shuffle"
            >
              <ShuffleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <button
              className="btn-icon"
              onClick={skipPrevious}
              aria-label="Previous"
            >
              <SkipPreviousIcon className="w-8 h-8 sm:w-10 sm:h-10" />
            </button>

            <button
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary text-on-primary
                         flex items-center justify-center hover:bg-primary-600 transition-colors"
              onClick={toggle}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <PauseIcon className="w-8 h-8 sm:w-10 sm:h-10" />
              ) : (
                <PlayIcon className="w-8 h-8 sm:w-10 sm:h-10 ml-1" />
              )}
            </button>

            <button
              className="btn-icon"
              onClick={skipNext}
              aria-label="Next"
            >
              <SkipNextIcon className="w-8 h-8 sm:w-10 sm:h-10" />
            </button>

            <button
              className={`btn-icon ${repeat !== 'off' ? 'text-primary' : ''}`}
              onClick={toggleRepeat}
              aria-label="Repeat"
            >
              <RepeatIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Secondary controls */}
          <div className="flex items-center gap-4">
            <button
              className={`btn-icon transition-colors ${liked ? 'text-red-500' : ''}`}
              onClick={() => toggleLike(currentSong)}
              aria-label={liked ? 'Unlike' : 'Like'}
            >
              {liked ? (
                <HeartIcon className="w-6 h-6" />
              ) : (
                <HeartOutlineIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
